import React, { useContext, useEffect, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { clone } from 'lodash';
import { i18nContext } from '@/components/pages/assessments/context/i18n.context';
import { displayContext } from '@/components/pages/assessments/context/display.context';
import { extractMappedValues } from '@/components/pages/assessments/util';
import SkipButton from '@/components/pages/assessments/common/SkipButton';

const NONE_OF_THE_ABOVE = 'None of the above';

const QuestionOptions = ({
  question,
  answer,
  setAnswer,
  setQuestionComplete,
  optionMapping,
  allowMultiple = false,
  includeNone = false,
  previewOnly = false
}) => {
  const { t } = useContext(i18nContext);
  const { css } = useContext(displayContext);
  const [values, setValues] = useState(answer || []);
  const [isNoneOfTheAbove, setIsNoneOfTheAbove] = useState(Array.isArray(answer) ? answer.includes(NONE_OF_THE_ABOVE) : answer === NONE_OF_THE_ABOVE);
  const [isSkipped, setIsSkipped] = useState(answer === null || (Array.isArray(answer) && answer.length === 0));
  const required = !!question.required;

  const handleClick = (opt, isNone, isSectionHeader) => {
    if (previewOnly || isSectionHeader) return;
    setIsSkipped(false);
    if (!allowMultiple) {
      setValues(opt);
      return setAnswer(opt);
    }
    if (isNone) {
      setIsNoneOfTheAbove(true);
      return setValues([NONE_OF_THE_ABOVE]);
    }
    // toggle opt in the list
    const selected = clone(values);
    let ii = selected.indexOf(opt);
    if (ii > -1) selected.splice(ii, 1);
    else selected.push(opt);
    // clear none-of-the-above if needed
    setIsNoneOfTheAbove(false);
    ii = selected.indexOf(NONE_OF_THE_ABOVE);
    if (ii > -1) selected.splice(ii, 1);
    setValues(selected);
  };

  const createOption = (opt, isNone = false) => {
    let sel = '';
    // 1.  Option is 'none' option and isNoneOfTheAbove is set
    // 2.  values is an array (for multi-select) and array includes opt (fixes bug where includes on a string value will partial match)
    // 3.  values is not an array and fully matches opt
    if ((isNone && isNoneOfTheAbove) || (Array.isArray(values) && values?.includes(opt)) || values == opt) {
      sel = css('selected');
    }

    const isSectionHeader = opt && opt.indexOf('**') == 0;
    const mappedValues = extractMappedValues(optionMapping, opt);
    return (
      <li
        key={opt}
        onClick={() => handleClick(opt, isNone, isSectionHeader)}
        className={css(['question-options-item', isSectionHeader ? 'question-options-item-header' : ''], sel)}
        style={previewOnly ? { pointerEvents: 'none', cursor: 'default' } : {}}
      >
        {allowMultiple && !isSectionHeader && (
          <div className={css('question-options-item-input')}>
            <Form.Check type={allowMultiple ? 'checkbox' : 'radio'} checked={sel !== ''} />
          </div>
        )}
        <div>
          <h5>{t(isNone ? NONE_OF_THE_ABOVE : opt.replace(/^\*\*/, ''))}</h5>
          {mappedValues?.length > 0 && <h6>{mappedValues.map(t).join(', ')}</h6>}
        </div>
      </li>
    );
  };

  const handleSkipClick = () => {
    if (!previewOnly) {
      setIsSkipped(true);
      setIsNoneOfTheAbove(false);
      setValues(allowMultiple ? [] : null);
    }
  };

  useEffect(() => {
    if (!previewOnly) {
      setQuestionComplete(isSkipped || (values !== null && values !== undefined && values?.length > 0));
      setAnswer(values);
    }
  }, [values, isSkipped, previewOnly]);

  const noneOpt = includeNone ? createOption(null, true) : null;

  return (
    <>
      <div className={`${css('question-input')} ${css('question-options')}`}>
        <ul>
          {question.value_options.map((opt) => createOption(opt))}
          {noneOpt}
        </ul>
        {!required && !previewOnly && <SkipButton isSkipped={isSkipped} onButtonClick={handleSkipClick} />}
      </div>
    </>
  );
};

export default QuestionOptions;
